import { useEffect, useRef, useState } from "react";
import { LoaderFunction, MetaFunction } from "react-router";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import godData from "~/data/about-us/god.json";
import { useFontStyle } from "~/hook/useFontStyle";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { LANG } from "~/utils/constant";
import { homeRouteString } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";

export const meta: MetaFunction = ({ location, params }) => {
  const lang = params.lang || "en";
  const pathname = location?.pathname || "/";
  const meta = getPageMeta("god", lang);

  return metaService({
    title: meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    canonicalLink: pathname,
    hreflangs: godData.hreflangs.map((lang) => ({
      ...lang,
      href: `${lang.href}${pathname}`,
    })),
  });
};

export const loader: LoaderFunction = ({ params }) => {
  const lang = params.lang || "en";
  return Response.json({
    title: lang === LANG.english ? "Swaminarayan Bhagwan" : "સ્વામિનારાયણ ભગવાન",
  });
};

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
        ]}
      />
    );
  },
};

// Intersection Observer Hook for scroll animations
const useIntersectionObserver = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold },
    );

    const currentRef = ref.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return [ref, isVisible] as const;
};

// Hero Section with Video
const HeroSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { medium } = useFontStyle();

  return (
    <section ref={ref} className={`relative w-full mb-5 md:mb-20 transition-all duration-700 ${isVisible ? "animate-fade-in-down" : "opacity-0"}`}>
      <div className="w-full flex justify-center items-center px-4 md:px-6">
        <div className="w-full max-w-4xl">
          {/* Video Embed */}
          <div className="rounded-xl overflow-hidden shadow-md border-2 border-primaryColor/20 lg:hover:shadow-2xl transition-shadow duration-300 group">
            <iframe
              className="w-full aspect-video rounded-xl lg:group-hover:brightness-110 transition-all duration-300"
              src="https://www.youtube.com/embed/j4Ga3TnAt0U"
              title="A Brief Introduction of Lord Swaminarayan"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <h1
            className={`text-center mt-6 text-2xl md:text-4xl font-bold text-textColor ${medium} lg:hover:text-primaryColor transition-colors duration-300`}
          >
            {godData.content.videoLink.title}
          </h1>
        </div>
      </div>
    </section>
  );
};

// Introduction Section
const IntroductionSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter } = useFontStyle();

  return (
    <section ref={ref} className={`mb-5 md:mb-20 px-4 md:px-6 transition-all duration-700 ${isVisible ? "animate-fade-in-up" : "opacity-0"}`}>
      <div className="max-w-4xl mx-auto">
        <div className="space-y-6">
          {godData.content.introduction.map((paragraph, index) => (
            <div
              key={index}
              className="group"
              style={{
                animation: isVisible ? `fadeInUp 0.6s ease-out ${index * 0.1}s forwards` : "none",
              }}
            >
              <p
                className={`text-textSecondaryColor text-base md:text-lg leading-relaxed text-justify ${normalInter} lg:hover:text-textColor transition-colors duration-300`}
              >
                {paragraph}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Vedic Evidence Section with Cards
const VedicEvidenceSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { medium, normalInter } = useFontStyle();

  return (
    <section ref={ref} className={`mb-12 md:mb-20 px-4 md:px-6 transition-all duration-700  ${isVisible ? "animate-fade-in-up" : "opacity-0"}`}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 md:mb-12">
          <h2
            className={`text-2xl md:text-3xl font-bold text-textColor ${medium} mb-4 inline-block border-b-4 border-primaryColor pb-2 lg:hover:text-primaryColor transition-colors duration-300`}
          >
            {godData.content.vedicEvidence.title}
          </h2>
          <p className={`text-textSecondaryColor text-base md:text-lg leading-relaxed text-justify ${normalInter} mt-6`}>
            {godData.content.vedicEvidence.introduction}
          </p>
        </div>

        {/* Scripture Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {godData.content.vedicEvidence.scriptures.map((scripture, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br from-cardBg via-white to-secondaryColor/30 p-6 md:p-8 rounded-lg border-l-4 border-primaryColor shadow-md lg:hover:shadow-lg transition-all duration-300 transform lg:hover:-translate-y-2 group ${
                isVisible ? "animate-scale-in" : "opacity-0"
              }`}
              style={{
                animation: isVisible ? `scaleIn 0.5s ease-out ${index * 0.1}s forwards` : "none",
              }}
            >
              <h3
                className={`text-lg md:text-xl font-bold text-textColor ${medium} mb-4 line-clamp-2 lg:group-hover:text-primaryColor transition-colors duration-300`}
              >
                {scripture.title}
              </h3>

              {/* Sanskrit with styling */}
              <div className="bg-white/60 p-4 rounded-lg mb-4 max-h-fit border border-primaryColor/10 lg:hover:border-primaryColor/30 transition-all duration-300">
                <p
                  className={`text-textSecondaryColor text-sm md:text-base ${normalInter} text-center leading-relaxed lg:group-hover:text-textColor transition-colors duration-300`}
                  dangerouslySetInnerHTML={{ __html: scripture.sanskrit }}
                />
              </div>

              {/* Transliteration */}
              <p
                className={`text-textSecondaryColor text-xs md:text-sm ${normalInter} italic mb-4 p-3 bg-gradient-to-r from-primaryColor/5 to-secondaryColor/20 rounded border border-primaryColor/10`}
              >
                <span dangerouslySetInnerHTML={{ __html: scripture.transliteration }} />
              </p>

              {/* Translation */}
              <p
                className={`text-textSecondaryColor text-sm md:text-base leading-relaxed ${normalInter} pl-4 border-l-2 border-primaryColor/50 lg:group-hover:border-primaryColor transition-colors duration-300`}
              >
                {scripture.translation}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Accomplishments Section
const AccomplishmentsSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { medium, normalInter } = useFontStyle();

  return (
    <section ref={ref} className={`mb-12 md:mb-20 px-4 md:px-6 transition-all duration-700 ${isVisible ? "animate-fade-in-up" : "opacity-0"}`}>
      <div className="max-w-4xl mx-auto">
        <div className="bg-gradient-to-r from-primaryColor/15 via-transparent to-secondaryColor/20 rounded-2xl p-8 md:p-12 border-l-4 border-primaryColor shadow-md lg:hover:shadow-lg transition-all duration-300 lg:hover:from-primaryColor/20 group">
          <h2
            className={`text-2xl md:text-3xl font-bold text-textColor ${medium} mb-6 inline-block border-b-4 border-primaryColor pb-2 lg:group-hover:text-primaryColor transition-colors duration-300`}
          >
            {godData.content.accomplishments.title}
          </h2>
          <p
            className={`text-textSecondaryColor text-base md:text-lg leading-relaxed text-justify ${normalInter} mt-6 lg:group-hover:text-textColor transition-colors duration-300`}
          >
            {godData.content.accomplishments.content}
          </p>
        </div>
      </div>
    </section>
  );
};

// Timeline Section
const TimelineSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const { medium, normalInter } = useFontStyle();

  return (
    <section ref={ref} className={`mb-12 md:mb-20 px-4 md:px-6 transition-all duration-700  ${isVisible ? "animate-fade-in-up" : "opacity-0"}`}>
      <div className="max-w-4xl mx-auto ">
        <h2
          className={`text-2xl md:text-3xl font-bold text-textColor ${medium} mb-10 md:mb-10 inline-block border-b-4 border-primaryColor pb-2 lg:hover:text-primaryColor transition-colors duration-300`}
        >
          {godData.content.timeline.title}
        </h2>

        {/* Timeline Container */}
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-primaryColor via-primaryColor to-secondaryColor " />

          {/* Timeline Events */}
          <div className="space-y-8 md:space-y-10">
            {godData.content.timeline.events.map((event, index) => (
              <div
                key={index}
                className={`relative md:flex md:items-start transition-all duration-500 ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
                style={{
                  animation: isVisible ? `fadeInUp 0.6s ease-out ${index * 0.1}s forwards` : "none",
                }}
              >
                {/* Timeline Dot */}
                <div className="absolute left-2.5 md:absolute md:left-1/2 md:-translate-x-1/2 top-0 md:top-0 w-4 h-4 bg-primaryColor rounded-full border-4 border-white shadow-md z-10 group-hover:scale-125 transition-transform duration-300" />

                {/* Content */}
                <div
                  className={`ml-12 md:ml-0 md:w-1/2 transition-all duration-300 group py-2 ${
                    index % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12 md:text-left"
                  }`}
                >
                  <button
                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpandedIndex(expandedIndex === index ? null : index);
                      }
                    }}
                    className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor rounded-xl"
                  >
                    <div
                      className={`p-2 md:p-4 rounded-xl border-2 transition-all duration-300 transform ${
                        expandedIndex === index
                          ? "bg-gradient-to-br from-cardBg via-white to-secondaryColor/30 border-primaryColor shadow-2xl scale-105 border-b-4"
                          : "bg-white border-lightGrayBorder lg:hover:border-primaryColor/50 shadow-md lg:hover:shadow-xl scale-100"
                      }`}
                    >
                      {/* Image - Show first */}
                      {event.image && (
                        <div className="mb-4 rounded-xl overflow-hidden shadow-md border-2 border-primaryColor/20 lg:hover:border-primaryColor transition-colors duration-300">
                          <img
                            src={event.image.src}
                            alt={event.image.alt}
                            className="w-full aspect-video object-cover lg:hover:scale-105 transition-transform duration-300 select-none"
                            loading="lazy"
                          />
                          <p
                            className={`text-sm text-textSecondaryColor ${normalInter} p-3 bg-gradient-to-r from-secondaryColor to-primaryColor/10 text-center italic`}
                          >
                            {event.image.alt}
                          </p>
                        </div>
                      )}

                      {/* Date */}
                      <h3
                        className={`text-lg md:text-xl font-bold text-primaryColor ${medium} mb-2 lg:group-hover:text-textColor transition-colors duration-300`}
                      >
                        {event.date}
                      </h3>

                      {/* Description - Consistent font size */}
                      <p
                        className={`text-sm md:text-base text-textSecondaryColor ${normalInter} transition-all duration-300 overflow-hidden leading-relaxed text-justify ${
                          expandedIndex === index ? "max-h-96" : "line-clamp-2"
                        }`}
                      >
                        {event.description}
                      </p>

                      {/* Expand Indicator */}
                      <div className="mt-3 flex items-center justify-between md:justify-start gap-2">
                        <span className={`inline-block text-xs ${normalInter} text-primaryColor font-semibold`}>
                          {expandedIndex === index ? "Show Less" : "Read More"}
                        </span>
                        <svg
                          className={`w-4 h-4 text-primaryColor transition-transform duration-300 ${expandedIndex === index ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

function God() {
  return (
    <div className="w-full bg-gradient-to-b from-white via-secondaryColor/5 to-white">
      <div className="w-full py-2 md:py-14">
        <HeroSection />
        <IntroductionSection />
        <VedicEvidenceSection />
        <AccomplishmentsSection />
        <TimelineSection />

        {/* Footer Spacing */}
        {/* <div className="h-12 md:h-20" /> */}
      </div>
    </div>
  );
}

export default God;
